import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { AxiosError } from "axios";
import addressApi from "api/landlord/address";
import { ApplyBrandingResponse } from "types/landlord/address";
import { useUnitsPropertiesDictionary } from "shared/hooks/use-units-properties";
import { propertiesLabel } from "views/properties-id/components/ApplyBrandingToProperties/helpers";

// After this many consecutive poll failures (~10s at the 1s interval below), stop
// auto-refetching instead of hammering a durably-broken endpoint (e.g. a stale
// bulkApplyId restored from localStorage that now 404s) forever. The panel still shows
// the error and offers Dismiss — this only stops the background request storm. Driven
// off react-query's own query.state.fetchFailureCount (reset by react-query on any
// successful fetch) rather than a hand-rolled ref+effect — isStatusError/status don't
// change value across repeated identical failures, so a dep-array effect watching them
// only fires once and can't count consecutive failures at all.
const MAX_CONSECUTIVE_POLL_ERRORS = 10;

// A run that keeps polling successfully but never reaches a settled status (stuck
// "processing" forever — a truly dead job, not just a slow one) stops being watched
// after this long, so a genuinely undead run can't poll every 1s for an entire session.
// Generously long on purpose: a real apply_to_all run over a large partner can
// legitimately take many minutes.
const MAX_POLL_DURATION_MS = 30 * 60 * 1000;

const CANCEL_ERROR_MESSAGES: Record<string, string> = {
  not_cancellable: "This run can't be cancelled.",
  already_settled: "This run has already finished."
};

const getCancelErrorMessage = (error: unknown) => {
  const code =
    error instanceof AxiosError ? error.response?.data?.error?.code : undefined;
  return (
    (code && CANCEL_ERROR_MESSAGES[code]) ||
    "Failed to cancel — it may already be finishing up"
  );
};

type BulkBrandingRun = {
  sourcePropertyId: number;
  bulkApplyId: number;
  // When tracking started, so MAX_POLL_DURATION_MS is measured from the run's actual
  // start rather than from whenever the app happens to remount — otherwise every reload
  // would hand a stuck run a fresh 30-minute window instead of the cap ever landing.
  startedAt: number;
};

// eslint-disable-next-line react-refresh/only-export-components
export const isSettledStatus = (status: ApplyBrandingResponse["status"]) =>
  status === "completed" || status === "failed" || status === "cancelled";

// eslint-disable-next-line react-refresh/only-export-components
export const isRunActive = (
  run: object | null,
  status: { status: ApplyBrandingResponse["status"] } | undefined
) => run !== null && !(status && isSettledStatus(status.status));

// Persisted across reloads (CRM-1215) — otherwise a reload or a re-mount of the app loses
// the in-memory run id and there's no way to resume watching a still-in-flight job. Cleared
// only on dismiss, so a settled-but-not-yet-dismissed run also survives a reload.
const STORAGE_KEY = "bulkBrandingProgress.run";

const readStoredRun = (): BulkBrandingRun | null => {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw);
    const isValid =
      typeof parsed?.sourcePropertyId === "number" &&
      typeof parsed?.bulkApplyId === "number" &&
      typeof parsed?.startedAt === "number";
    return isValid ? parsed : null;
  } catch {
    return null;
  }
};

const writeStoredRun = (run: BulkBrandingRun | null) => {
  try {
    if (run) window.localStorage.setItem(STORAGE_KEY, JSON.stringify(run));
    else window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // localStorage unavailable (private mode, quota) — tracking just won't survive a reload.
  }
};

type BulkBrandingProgressContext = {
  run: BulkBrandingRun | null;
  status: ApplyBrandingResponse | undefined;
  isStatusError: boolean;
  hasGivenUpPolling: boolean;
  isCancelling: boolean;
  hasRequestedCancel: boolean;
  startTracking: (run: Omit<BulkBrandingRun, "startedAt">) => void;
  cancelRun: () => void;
  dismiss: () => void;
};

const BulkBrandingProgressContext = createContext<BulkBrandingProgressContext>({
  run: null,
  status: undefined,
  isStatusError: false,
  hasGivenUpPolling: false,
  isCancelling: false,
  hasRequestedCancel: false,
  startTracking: () => {},
  cancelRun: () => {},
  dismiss: () => {}
});

// Tracks a bulk-branding-apply run independently of ApplyBrandingModal (CRM-1215) — the
// modal only enqueues the run and hands off the id here, so progress survives the modal
// closing and the user navigating elsewhere. BulkBrandingProgressPanel renders the result.
export function BulkBrandingProgressProvider({
  children
}: {
  children: ReactNode;
}) {
  const queryClient = useQueryClient();
  const dictionary = useUnitsPropertiesDictionary();
  const [run, setRun] = useState<BulkBrandingRun | null>(readStoredRun);
  const settledRunIdRef = useRef<number | null>(null);
  // False for a run just resumed from localStorage until its first poll actually comes
  // back unsettled — guards the settle effect below from re-toasting on every reload of
  // a run that already settled (and was seen settling) before a prior reload, while
  // still firing normally for a run that's genuinely still in flight.
  const hasObservedUnsettledRef = useRef(false);
  // True once a cancel request has round-tripped successfully — cancellation is
  // cooperative, so the run is still "processing" (cancellable stays true) until the
  // batch job actually honors it, and this bridges that gap so the button doesn't look
  // clickable again in the meantime. Reset per-run in startTracking.
  const [hasRequestedCancel, setHasRequestedCancel] = useState(false);

  const statusQueryKey = [
    "properties",
    run?.sourcePropertyId,
    "apply_branding",
    run?.bulkApplyId
  ];

  const { data: status, isError: isStatusError } =
    useQuery<ApplyBrandingResponse>({
      queryKey: statusQueryKey,
      queryFn: () =>
        addressApi.getApplyBrandingStatus({
          propertyId: run!.sourcePropertyId,
          bulkApplyId: run!.bulkApplyId
        }),
      enabled: run !== null,
      refetchInterval: (query) => {
        const s = query.state.data?.status;
        if (s && isSettledStatus(s)) return false;
        if (query.state.fetchFailureCount >= MAX_CONSECUTIVE_POLL_ERRORS)
          return false;
        if (run && Date.now() - run.startedAt > MAX_POLL_DURATION_MS)
          return false;
        return 1000;
      }
    });

  // Recomputed every render (cheap) rather than tracked in state — the interval stopping
  // itself doesn't otherwise trigger a re-render, but the fetch immediately before the
  // cutoff always does, and that's the render this needs to become true in.
  const hasGivenUpPolling = Boolean(
    run &&
    status &&
    !isSettledStatus(status.status) &&
    Date.now() - run.startedAt > MAX_POLL_DURATION_MS
  );

  const { mutate: cancelRun, isPending: isCancelling } = useMutation({
    mutationKey: ["BULK", "APPLY", "BRANDING", "CANCEL"],
    mutationFn: () =>
      addressApi.cancelApplyBranding({
        propertyId: run!.sourcePropertyId,
        bulkApplyId: run!.bulkApplyId
      }),
    onSuccess: () => {
      setHasRequestedCancel(true);
      queryClient.invalidateQueries({ queryKey: statusQueryKey });
    },
    onError: (mutationError) =>
      toast.error(getCancelErrorMessage(mutationError))
  });

  useEffect(() => {
    if (!status) return;
    if (!isSettledStatus(status.status)) {
      hasObservedUnsettledRef.current = true;
      return;
    }
    if (settledRunIdRef.current === status.id) return;

    settledRunIdRef.current = status.id;
    queryClient.invalidateQueries({ queryKey: ["properties"] });

    // A run resumed from storage that turns out to already be settled was already
    // toasted about before whatever reload brought us here — only the (always-safe)
    // invalidate above repeats; the toast doesn't.
    if (!hasObservedUnsettledRef.current) return;

    if (status.status === "cancelled")
      toast.info(
        `Branding cancelled — ${status.updated_count} ${propertiesLabel(status.updated_count, dictionary)} already updated were kept`
      );
    else if (status.status === "completed" && status.failed.length === 0)
      toast.success(
        `Branding applied to ${status.updated_count} ${propertiesLabel(status.updated_count, dictionary)}`
      );
  }, [status, queryClient, dictionary]);

  const startTracking = useCallback(
    (next: Omit<BulkBrandingRun, "startedAt">) => {
      const withStartedAt = { ...next, startedAt: Date.now() };
      settledRunIdRef.current = null;
      hasObservedUnsettledRef.current = true;
      setHasRequestedCancel(false);
      writeStoredRun(withStartedAt);
      setRun(withStartedAt);
    },
    []
  );

  const dismiss = useCallback(() => {
    writeStoredRun(null);
    setRun(null);
  }, []);

  const value = useMemo(
    () => ({
      run,
      status,
      isStatusError,
      hasGivenUpPolling,
      isCancelling,
      hasRequestedCancel,
      startTracking,
      cancelRun,
      dismiss
    }),
    [
      run,
      status,
      isStatusError,
      hasGivenUpPolling,
      isCancelling,
      hasRequestedCancel,
      startTracking,
      cancelRun,
      dismiss
    ]
  );

  return (
    <BulkBrandingProgressContext.Provider value={value}>
      {children}
    </BulkBrandingProgressContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useBulkBrandingProgress() {
  return useContext(BulkBrandingProgressContext);
}
